import * as React from 'react';
import { useIntl } from '@grandvisionhq/state';
import { useCancelAnonymousSubscription } from '../../hooks/api/cancelAnonymousSubscription/use-cancel-anonymous-subscription.js';
import { CancelSubscriptionForm } from './CancelSubscriptionForm.js';

const CancelSubscriptionFormContainer = ()=>{
    const [subscriptionNumber1, setSubscriptionNumber] = React.useState('');
    const [email1, setEmail] = React.useState('');
    const [isSuccess, setIsSuccess] = React.useState(false);
    const [selectedCancellationType, setSelectedCancellationType] = React.useState('');
    const { getLabel , getFormattedLabel , dateTime  } = useIntl();
    const formRef = React.useRef(null);
    // Labels are the only object passed to the form component
    // "heavy" computation in the getFormattedLabel and format dateTime utility
    const labels = React.useMemo(()=>({
            subscriptionNumber: {
                label: getLabel('cancelSubscription.field.subscriptionNumber'),
                placeholder: getLabel('cancelSubscription.field.subscriptionNumber.placeholder'),
                errorMessage: getLabel('cancelSubscription.field.subscriptionNumber.errorMessage')
            },
            email: {
                label: getLabel('cancelSubscription.field.email'),
                placeholder: getLabel('cancelSubscription.field.email.placeholder'),
                errorMessage: getLabel('cancelSubscription.field.email.errorMessage')
            },
            postalCode: {
                label: getLabel('cancelSubscription.field.postalCode'),
                placeholder: getLabel('cancelSubscription.field.postalCode.placeholder'),
                errorMessage: getLabel('cancelSubscription.field.postalCode.errorMessage')
            },
            cancellationDate: {
                label: getLabel('cancelSubscription.field.cancellationDate'),
                placeholder: dateTime.formatMidsizeDate(new Date()),
                errorMessage: getLabel('cancelSubscription.field.cancellationDate.errorMessage')
            },
            cancellationType: {
                label: getLabel('cancelSubscription.field.cancellationType'),
                placeholder: getLabel('cancelSubscription.field.cancellationType.placeholder'),
                errorMessage: getLabel('cancelSubscription.field.cancellationType.errorMessage'),
                options: {
                    ordinary: getLabel('cancelSubscription.field.cancellationType.ordinary'),
                    extraordinary: getLabel('cancelSubscription.field.cancellationType.extraordinary')
                }
            },
            reason: {
                label: getLabel('cancelSubscription.field.reason'),
                placeholder: getLabel('cancelSubscription.field.reason.placeholder'),
                errorMessage: getLabel('cancelSubscription.field.reason.errorMessage'),
                options: {
                    changedPrescription: getLabel('cancelSubscription.field.reason.changedPrescription'),
                    notNeeded: getLabel('cancelSubscription.field.reason.notNeeded'),
                    tooExpensive: getLabel('cancelSubscription.field.reason.tooExpensive'),
                    foundAnotherProduct: getLabel('cancelSubscription.field.reason.foundAnotherProduct'),
                    deliveryProblems: getLabel('cancelSubscription.field.reason.deliveryProblems'),
                    subscriptionManagementProblems: getLabel('cancelSubscription.field.reason.subscriptionManagementProblems'),
                    other: getLabel('cancelSubscription.field.reason.other')
                }
            },
            submit: getLabel('cancelSubscription.action.submit'),
            success: {
                title: getFormattedLabel('cancelSubscription.success.title', {
                    labelData: {
                        subscriptionNumber: subscriptionNumber1
                    }
                }),
                description: getFormattedLabel('cancelSubscription.success.description', {
                    labelData: {
                        email: email1
                    }
                })
            }
        })
    , [
        getLabel,
        getFormattedLabel,
        dateTime,
        subscriptionNumber1,
        email1
    ]);
    const handleOnCompleted = React.useCallback(()=>{
        var ref;
        setIsSuccess(true);
        (ref = formRef.current) === null || ref === void 0 ? void 0 : ref.resetForm();
        globalThis.window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);
    const { cancelAnonymousSubscription , isUpdating  } = useCancelAnonymousSubscription({
        onCompleted: handleOnCompleted
    });
    const handleSubmit = React.useCallback(({ formState  })=>{
        if (formState.subscriptionNumber && formState.email && formState.postalCode && formState.cancellationDate && formState.cancellationType && (formState.reasonOrdinary || formState.reasonExtraordinary)) {
            var ref, ref1;
            const email = formState.email.value.toLowerCase();
            const subscriptionNumber = formState.subscriptionNumber.value;
            const postalCode = formState.postalCode.value;
            const cancellationDate = formState.cancellationDate.value;
            const cancellationType = formState.cancellationType.value;
            var ref2, ref3;
            const reason = cancellationType === 'ORDINARY' ? (ref2 = (ref = formState.reasonOrdinary) === null || ref === void 0 ? void 0 : ref.value) !== null && ref2 !== void 0 ? ref2 : '' : (ref3 = (ref1 = formState.reasonExtraordinary) === null || ref1 === void 0 ? void 0 : ref1.value) !== null && ref3 !== void 0 ? ref3 : '';
            setIsSuccess(false);
            // For the success message we both number and email
            setSubscriptionNumber(subscriptionNumber);
            setEmail(email);
            cancelAnonymousSubscription(subscriptionNumber, {
                email,
                postalCode,
                cancelDate: cancellationDate,
                cancelType: cancellationType,
                cancelReason: reason
            });
        }
    }, [
        cancelAnonymousSubscription
    ]);
    const handleBlur = React.useCallback(()=>{
        // Reset the alert message data when the form is changed after a submission
        if (isSuccess) {
            setSubscriptionNumber('');
            setEmail('');
            setSelectedCancellationType('');
            setIsSuccess(false);
        }
    }, [
        isSuccess
    ]);
    const handleFormChange = React.useCallback(({ formState  })=>{
        var ref;
        if ((ref = formState.cancellationType) === null || ref === void 0 ? void 0 : ref.value) {
            const type = formState.cancellationType.value;
            if (type === 'ORDINARY' && selectedCancellationType !== 'ORDINARY') {
                var ref4, ref5;
                setSelectedCancellationType('ORDINARY');
                (ref4 = formRef.current) === null || ref4 === void 0 ? void 0 : ref4.removeFieldState('reasonExtraordinary');
                (ref5 = formRef.current) === null || ref5 === void 0 ? void 0 : ref5.setFieldState({
                    name: 'reasonOrdinary',
                    isTouched: false,
                    isValid: false,
                    value: ''
                });
            }
            if (type === 'EXTRAORDINARY' && selectedCancellationType !== 'EXTRAORDINARY') {
                var ref6, ref7;
                setSelectedCancellationType('EXTRAORDINARY');
                (ref6 = formRef.current) === null || ref6 === void 0 ? void 0 : ref6.removeFieldState('reasonOrdinary');
                (ref7 = formRef.current) === null || ref7 === void 0 ? void 0 : ref7.setFieldState({
                    name: 'reasonExtraordinary',
                    isTouched: false,
                    isValid: false,
                    value: ''
                });
            }
        }
    }, [
        selectedCancellationType
    ]);
    return /*#__PURE__*/ React.createElement(CancelSubscriptionForm, {
        formRef: formRef,
        isSuccess: isSuccess,
        isUpdating: isUpdating,
        labels: labels,
        onBlur: handleBlur,
        onChange: handleFormChange,
        onSubmit: handleSubmit,
        selectedCancellationType: selectedCancellationType
    });
};

export { CancelSubscriptionFormContainer };
//# sourceMappingURL=CancelSubscriptionFormContainer.js.map
